// src/components/MsoNewDraftPanel.tsx
//
// ★★ ANNEX A 22 — start a draft: a new VERSION of an existing rule, or a brand-new MSO scenario.
//
// A draft is the reversible half of the lifecycle (it can be edited, tested and discarded by never
// activating it), so it does not go through `ConfirmWrite`; ACTIVATING it, which prices bills,
// does, on the editor.
//
// ⚠ **A new scenario starts as a COPY of a rule that already works.** An empty definition is not
// a valid rule, and a blank grid is the worst place to start authoring money. The copy is made
// here from the definition the list already holds; the route validates it again regardless.
//
// ⚠ **The copy is re-keyed.** Its decision-row ids carry the source rule's prefix (`R1-PRINCIPAL`), so they are
// rewritten to the new key's (`R4-PRINCIPAL`) before the draft is created: a trace naming a row of another rule
// is a trace nobody can trust.
//
// ⚠ **Two refusals belong at a field, not in a banner.** `RULE_EXISTS` (the key is already a rule) is shown at
// the key; `BACKDATED_OVER_PRICED` (the start date would reach back over a bill already priced) at the date.
//
// ⚠ **When a draft already exists the refusal is not a dead end.** The route answers
// `DRAFT_EXISTS` with the id of the open draft, and the card offers to open it: a refusal with no
// way forward is the same failure wearing a better sentence.
import { useId, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { rewriteRowIds } from '../lib/mso-editor'
import { useDisclosureFocus } from '../lib/useDisclosureFocus'
import { DatePicker } from './DatePicker'
import { fieldErrorProps, FieldError } from './FieldError'
import { FormResult, type FormResultState } from './FormResult'
import type { MsoFieldBounds, MsoRuleSummary } from '../lib/types'

export type NewDraftMode = { readonly kind: 'version'; readonly rule: MsoRuleSummary } | { readonly kind: 'scenario' }

export interface MsoNewDraftPanelProps {
  readonly mode: NewDraftMode
  /** Every rule, so a new scenario can start as a copy of one of them. */
  readonly rules: readonly MsoRuleSummary[]
  /** The server's published length bounds. Absent on a gateway that does not publish them: then none is shown. */
  readonly bounds?: MsoFieldBounds | undefined
  readonly onClose: () => void
}

export function MsoNewDraftPanel({ mode, rules, bounds, onClose }: MsoNewDraftPanelProps) {
  const baseId = useId()
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)
  // Focus moves into the form, Escape closes it and gives focus back to the control that opened it (F-05).
  useDisclosureFocus(formRef, onClose)
  const copyable = [...rules.filter((r) => r.active !== null)].sort((a, b) => (a.ruleKey < b.ruleKey ? -1 : 1))
  const [ruleKey, setRuleKey] = useState('')
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [copyFrom, setCopyFrom] = useState(copyable[0]?.ruleKey ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [state, setState] = useState<FormResultState>({ status: 'idle' })
  const [existingDraft, setExistingDraft] = useState<string | null>(null)
  const [keyProblem, setKeyProblem] = useState<string | null>(null)
  const [dateProblem, setDateProblem] = useState<string | null>(null)

  const scenario = mode.kind === 'scenario'
  const canSubmit =
    effectiveFrom.length > 0 &&
    (!scenario || (ruleKey.trim().length > 0 && name.trim().length > 0 && purpose.trim().length > 0 && copyFrom.length > 0))

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setState({ status: 'submitting' })
    setExistingDraft(null)
    setKeyProblem(null)
    setDateProblem(null)
    try {
      const source = scenario ? copyable.find((r) => r.ruleKey === copyFrom)?.active : mode.rule.active
      const body =
        mode.kind === 'version'
          ? {
              ruleKey: mode.rule.ruleKey,
              effectiveFrom,
              ...(source !== null && source !== undefined && { basedOnVersionId: source.id }),
            }
          : {
              ruleKey: ruleKey.trim().toUpperCase(),
              name: name.trim(),
              purpose: purpose.trim(),
              effectiveFrom,
              ...(source !== null && source !== undefined && {
                definition: rewriteRowIds(source.definition, source.ruleKey, ruleKey.trim().toUpperCase()),
              }),
            }
      const created = await api.createMsoDraft(body)
      navigate(`/administration/mso-rules/versions/${created.version.id}`)
    } catch (error) {
      if (error instanceof ApiError) {
        const draftId = error.detail['draftId']
        if (typeof draftId === 'string') setExistingDraft(draftId)
        const outcome = error.detail['outcome']
        if (outcome === 'RULE_EXISTS') {
          setKeyProblem(error.message)
          setState({ status: 'idle' })
        } else if (outcome === 'BACKDATED_OVER_PRICED') {
          setDateProblem(error.message)
          setState({ status: 'idle' })
        } else {
          setState({ status: 'error', message: error.message })
        }
      } else {
        setState({ status: 'error', message: 'Could not create the draft.' })
      }
    }
  }

  const heading = scenario ? 'New MSO scenario' : `New draft of ${mode.kind === 'version' ? mode.rule.ruleKey : ''}`

  return (
    <form ref={formRef} className="pt-card flex flex-col gap-4" onSubmit={handleSubmit} noValidate aria-label={heading}>
      <h2 className="text-lg font-semibold">{heading}</h2>
      <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
        {scenario
          ? 'A new scenario is its own rule with its own versions. It starts as a copy of a rule that already works, which you then edit, test and have a second PSD officer activate. Nothing prices a bill until it is activated.'
          : 'A new version starts as a copy of the version in force. You edit it, test it and have a second PSD officer activate it. Nothing prices a bill until it is activated.'}
      </p>

      {scenario && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-key`} className="font-semibold">
              Rule key <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>
            <input
              id={`${baseId}-key`}
              className="pt-native-date"
              value={ruleKey}
              placeholder="e.g. MSO-R4"
              onChange={(e) => {
                setRuleKey(e.target.value)
                setKeyProblem(null)
              }}
              {...fieldErrorProps(`${baseId}-key-error`, keyProblem)}
            />
            <FieldError id={`${baseId}-key-error`} problem={keyProblem} />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-copy`} className="font-semibold">
              Start as a copy of
            </label>
            <select id={`${baseId}-copy`} className="pt-native-date" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              {copyable.map((r) => (
                <option key={r.ruleKey} value={r.ruleKey}>
                  {r.ruleKey}: {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-name`} className="font-semibold">
              Name <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>
            <input
              id={`${baseId}-name`}
              className="pt-native-date"
              value={name}
              placeholder="e.g. MSO polyclinic follow-up"
              {...(bounds !== undefined && { maxLength: bounds.ruleName })}
              aria-describedby={`${baseId}-name-count`}
              onChange={(e) => setName(e.target.value)}
            />
            {bounds !== undefined && (
              <span id={`${baseId}-name-count`} className="text-xs text-[var(--sgds-body-color-subtle)]">
                {bounds.ruleName - name.length} of {bounds.ruleName} characters left.
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor={`${baseId}-purpose`} className="font-semibold">
              Purpose <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>
            <input
              id={`${baseId}-purpose`}
              className="pt-native-date"
              value={purpose}
              placeholder="What this scenario prices and why"
              {...(bounds !== undefined && { maxLength: bounds.purpose })}
              aria-describedby={`${baseId}-purpose-count`}
              onChange={(e) => setPurpose(e.target.value)}
            />
            {bounds !== undefined && (
              <span id={`${baseId}-purpose-count`} className="text-xs text-[var(--sgds-body-color-subtle)]">
                {bounds.purpose - purpose.length} of {bounds.purpose} characters left.
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1 text-sm max-w-xs">
        <span id={`${baseId}-from`} className="font-semibold">
          Effective from <span aria-hidden="true">*</span>
          <span className="sr-only">required</span>
        </span>
        <DatePicker
          value={effectiveFrom}
          onChange={(v) => {
            setEffectiveFrom(v)
            setDateProblem(null)
          }}
          labelledBy={`${baseId}-from`}
          required
          subject="effective date"
          {...(dateProblem !== null && { invalid: true, describedBy: `${baseId}-from-error` })}
        />
        <FieldError id={`${baseId}-from-error`} problem={dateProblem} />
      </div>

      <FormResult state={state} />
      {existingDraft !== null && (
        <p className="text-sm">
          <Link to={`/administration/mso-rules/versions/${existingDraft}`}>Open the existing draft</Link>
        </p>
      )}

      <div className="flex gap-2">
        <SgdsButton type="submit" variant="primary" disabled={!canSubmit} loading={state.status === 'submitting'}>
          Create draft
        </SgdsButton>
        <SgdsButton type="button" variant="outline" onClick={onClose}>
          Cancel
        </SgdsButton>
      </div>
    </form>
  )
}
